import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import './Detail.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faShoppingCart, faUserMd, faClock, faHeart, faExternalLinkAlt, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

// Importar Swiper React components y estilos
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

import baseURL from '../url';
import moneda from '../moneda';
import DetailLoading from "../DetailLoading/DetailLoading";
import CarouselDetail from "./CarouselDetail";

export default function Detail() {
    const { idProducto } = useParams();
    const navigate = useNavigate();
    const swiperRef = useRef(null);

    // Estados básicos
    const [producto, setProducto] = useState(null);
    const [productos, setProductos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [profesionales, setProfesionales] = useState([]);
    const [loading, setLoading] = useState(true);

    // Estados de reserva
    const [categoriaEsServicio, setCategoriaEsServicio] = useState(false);
    const [profesionalSeleccionado, setProfesionalSeleccionado] = useState(null);
    const [diaSeleccionado, setDiaSeleccionado] = useState(new Date()); 
    const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
    const [fechasNoDisponibles, setFechasNoDisponibles] = useState([]);

    // Estados de UI
    const [selectedItems] = useState([]);
    const [favoritos, setFavoritos] = useState([]);

    const diasSemanaMap = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];

    useEffect(() => {
        fetchData();
        setFavoritos(JSON.parse(localStorage.getItem('favoritos')) || []);
    }, []);

    const toggleFavorito = () => {
        if (!producto) return;
        const index = favoritos.indexOf(producto.idProducto);
        let newFavoritos = [...favoritos];
        if (index === -1) {
            newFavoritos.push(producto.idProducto);
            toast.success("Añadido a favoritos", { position: "top-center", autoClose: 2000 });
        } else {
            newFavoritos.splice(index, 1);
            toast.info("Eliminado de favoritos", { position: "top-center", autoClose: 2000 });
        }
        setFavoritos(newFavoritos);
        localStorage.setItem('favoritos', JSON.stringify(newFavoritos));
    };

    const handleCompartir = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const shareUrl = `${window.location.origin}/producto/${producto.idProducto}/${producto.titulo.replace(/\s+/g, '-')}`;
        if (navigator.share) {
            navigator.share({
                title: producto.titulo,
                text: producto.descripcion,
                url: shareUrl,
            }).catch(err => console.log("Error sharing:", err));
        } else {
            navigator.clipboard.writeText(shareUrl);
            toast.success("¡Enlace del producto copiado!", { position: "top-center", autoClose: 2000 });
        }
    };

    const fetchData = async () => {
        try {
            const [resProd, resCat, resProf] = await Promise.all([
                fetch(`${baseURL}/productosGet.php`).then(res => res.json()),
                fetch(`${baseURL}/categoriasGet.php`).then(res => res.json()),
                fetch(`${baseURL}/profesionalesGet.php`).then(res => res.json())
            ]);
            setProductos(resProd.productos || []);
            setCategorias(resCat.categorias || []);
            setProfesionales(resProf.profesionales || []);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setLoading(false);
        }
    };

    useEffect(() => {
        const product = productos.find(e => e.idProducto === parseInt(idProducto));
        if (product) {
            setProducto(product);
            const cat = categorias.find(c => c.idCategoria === product.idCategoria);
            if (cat) {
                const normalizado = cat.categoria.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                
                const esServicioPorNombre = normalizado.includes("servicio") || 
                                           normalizado.includes("depilacion") || 
                                           normalizado.includes("estetica") || 
                                           normalizado.includes("peluqueria") || 
                                           normalizado.includes("reserva") || 
                                           normalizado.includes("turno") || 
                                           normalizado.includes("cita") || 
                                           normalizado.includes("masaje") || 
                                           normalizado.includes("spa") ||
                                           normalizado.includes("unas") ||
                                           normalizado.includes("cejas") ||
                                           normalizado.includes("pestanas") ||
                                           normalizado.includes("facial");
                                           
                const tieneConfiguracionDias = product.dias && product.dias !== "" && product.dias !== "{}";
                
                setCategoriaEsServicio(esServicioPorNombre || tieneConfiguracionDias);
            }
            cargarReservas(product.idProducto);
        }
    }, [idProducto, productos, categorias]);

    const cargarReservas = (id) => {
        fetch(`${baseURL}/getProductoReservas.php?idProducto=${id}`)
            .then(res => res.json())
            .then(data => data.success && setFechasNoDisponibles(data.reservas));
    };

    const profesionalesConConfiguracion = profesionales.filter(prof => {
        if (!producto?.dias) return false;
        const configRaw = typeof producto.dias === "string" ? JSON.parse(producto.dias) : producto.dias;
        const configProf = configRaw[prof.id];
        if (!configProf) return false;
        return Object.values(configProf).some(dia => 
            dia.turnos && dia.turnos.some(t => t.activo !== false)
        );
    });

    const listaDias = Array.from({ length: 30 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() + i);
        return d;
    });

    const obtenerTurnosDia = () => {
        if (!profesionalSeleccionado || !producto?.dias) return [];
        
        const configRaw = typeof producto.dias === "string" ? JSON.parse(producto.dias) : producto.dias;
        const configProf = configRaw[profesionalSeleccionado.id];
        const nombreDia = diasSemanaMap[diaSeleccionado.getDay()];
        
        if (!configProf || !configProf[nombreDia]?.turnos) return [];

        // 1. Formateamos la fecha seleccionada a YYYY-MM-DD para comparar fácilmente
        const anio = diaSeleccionado.getFullYear();
        const mes = String(diaSeleccionado.getMonth() + 1).padStart(2, '0');
        const dia = String(diaSeleccionado.getDate()).padStart(2, '0');
        const fechaSeleccionadaString = `${anio}-${mes}-${dia}`;

        return configProf[nombreDia].turnos.filter(t => t.activo !== false).map(t => {
            // 2. Verificamos si este turno específico ya existe en las reservas
            const estaOcupado = fechasNoDisponibles.some(r => {
                // Extraemos solo la parte de la fecha y hora de la reserva (YYYY-MM-DDTHH:mm)
                // Usamos .includes para evitar problemas con los segundos (:00)
                const reservaDiaYHora = r.start; // Ejemplo: "2025-12-29T11:00:00"
                
                const coincideFecha = reservaDiaYHora.includes(fechaSeleccionadaString);
                const coincideHora = reservaDiaYHora.includes(t.hora);
                
                // 3. VALIDACIÓN POR PROFESIONAL:
                // Si la reserva tiene pid, comparamos que sea el mismo profesional.
                // Si la reserva NO tiene pid (null), asumimos que bloquea el turno para todos o es un formato antiguo.
                const coincideProfesional = r.pid === null || parseInt(r.pid) === parseInt(profesionalSeleccionado.id);

                return coincideFecha && coincideHora && coincideProfesional;
            });

            return { 
                hora: t.hora, 
                ocupado: estaOcupado 
            };
        });
    };

    const handleAddToCart = () => {
    if (categoriaEsServicio && (!profesionalSeleccionado || !turnoSeleccionado)) {
        return toast.error("Selecciona profesional y horario");
    }

    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    let itemToAdd = {
        idProducto: producto.idProducto,
        titulo: producto.titulo,
        precio: producto.precio,
        cantidad: 1,
        items: selectedItems.map(index => producto[`item${index+1}`])
    };

    if (categoriaEsServicio) {
        // 1. Creamos la fecha base
        const dI = new Date(diaSeleccionado);
        const [hI, mI] = turnoSeleccionado.hora.split(":").map(Number);
        
        // Seteamos la hora local seleccionada
        dI.setHours(hI, mI, 0, 0);

        let demoraTotal = 60;
        try {
            const demoraProfesional = JSON.parse(producto.demora);
            if(profesionalSeleccionado.id && demoraProfesional[profesionalSeleccionado.id]){
                demoraTotal = demoraProfesional[profesionalSeleccionado.id];
            }
        } catch(e) { console.log("e", e); }

        const duracionMinutos = parseInt(demoraTotal);
        const dF = new Date(dI.getTime() + duracionMinutos * 60000);

        // --- SOLUCIÓN AL DESFASE HORARIO ---
        // En lugar de toISOString(), creamos el string manualmente para preservar la hora local
        const formatLocal = (date) => {
            const pad = (n) => n.toString().padStart(2, '0');
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
        };

        itemToAdd = {
            ...itemToAdd,
            profesionalId: profesionalSeleccionado?.id,
            profesionalNombre: profesionalSeleccionado?.nombre,
            // Guardamos el formato local "YYYY-MM-DDTHH:mm:00"
            fecha_inicio: formatLocal(dI),
            fecha_fin: formatLocal(dF)
        };
    }
    cart.push(itemToAdd);
    localStorage.setItem('cart', JSON.stringify(cart));
    localStorage.setItem('openCart', 'true');
    toast.success("¡Agregado!");
    
    setTimeout(() => {
        window.location.href = '/';
    }, 500);
};

    if (loading || !producto) return <DetailLoading />;

    return (
        <div className="detail">
            <button className="back" onClick={() => navigate(-1)}><FontAwesomeIcon icon={faArrowLeft} /></button>

            <div className="detail-contain">
                <CarouselDetail producto={producto} />

                <div className="textDetail">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                        <h2 className="title" style={{ margin: 0 }}>{producto.titulo}</h2>
                        <div className="action-buttons-detail">
                            <button 
                                onClick={handleCompartir} 
                                title="Compartir producto"
                                className="btn-share-detail"
                            >
                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                            </button>
                            <button 
                                onClick={toggleFavorito} 
                                title="Añadir a favoritos"
                                className={`btn-fav-detail ${favoritos.includes(producto.idProducto) ? 'active' : ''}`}
                            >
                                <FontAwesomeIcon icon={faHeart} />
                            </button>
                        </div>
                    </div>
                    <h5 className="price">{moneda} {producto.precio.toLocaleString()}</h5>
                    <p className="descripcion">{producto.descripcion}</p>

                    {categoriaEsServicio && (
                        <div className="Dias">
                            <h3 className="titulo-reserva"><FontAwesomeIcon icon={faUserMd} /> 1. Seleccioná Profesional</h3>
                            <div className="profesionales-grid">
                                {profesionalesConConfiguracion.map(prof => (
                                    <div key={prof.id} 
                                         className={`prof-card ${profesionalSeleccionado?.id === prof.id ? 'active' : ''}`}
                                         onClick={() => {setProfesionalSeleccionado(prof); setTurnoSeleccionado(null)}}>
                                        <div className="prof-avatar">{prof.nombre[0].toUpperCase()}</div>
                                        <span>{prof.nombre}</span>
                                    </div>
                                ))}
                            </div>

                            {profesionalSeleccionado && (
                                <div className="fade-in">
                                    <h3 className="titulo-reserva"><FontAwesomeIcon icon={faClock} /> 2. Seleccioná Día y Hora</h3>
                                    <div className="swiper-navigation-wrapper">
                                        <button 
                                            type="button"
                                            className="swiper-nav-btn prev" 
                                            onClick={() => swiperRef.current?.slidePrev()}
                                            title="Anterior"
                                        >
                                            <FontAwesomeIcon icon={faChevronLeft} />
                                        </button>

                                        <Swiper 
                                            onSwiper={(swiper) => { swiperRef.current = swiper; }}
                                            slidesPerView={'auto'} 
                                            className="swiper-container" 
                                            id="cardsServicio"
                                        >
                                            {listaDias.map((date, idx) => (
                                                <SwiperSlide key={idx} style={{ width: 'auto' }}>
                                                    <div id={diaSeleccionado.toDateString() === date.toDateString() ? "selectedDay" : "cardDay"}
                                                         onClick={() => {setDiaSeleccionado(date); setTurnoSeleccionado(null)}}>
                                                        <h5>{diasSemanaMap[date.getDay()] === "domingo" ? "domingo y/o festivo" : diasSemanaMap[date.getDay()]}</h5>
                                                        <h4>{date.getDate()}</h4>
                                                        <h5>{date.toLocaleDateString('es-ES', { month: 'short' })}</h5>
                                                        <div className="green"></div>
                                                    </div>
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>

                                        <button 
                                            type="button"
                                            className="swiper-nav-btn next" 
                                            onClick={() => swiperRef.current?.slideNext()}
                                            title="Siguiente"
                                        >
                                            <FontAwesomeIcon icon={faChevronRight} />
                                        </button>
                                    </div>

                                    <div className="horariosSeleccionados">
                                        <div className="flexGrapHoras">
                                            {obtenerTurnosDia().map((t, idx) => (
                                                <button key={idx} 
                                                    disabled={t.ocupado}
                                                    className={turnoSeleccionado?.hora === t.hora ? "btn-hora-active" : ""}
                                                    style={{ opacity: t.ocupado ? 0.4 : 1 }}
                                                    onClick={() => setTurnoSeleccionado(t)}>
                                                    {t.hora}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <button onClick={handleAddToCart} className="btnAdd">
                        {categoriaEsServicio ? "Reservar Turno" : "Agregar al carrito"} <FontAwesomeIcon icon={faShoppingCart} />
                    </button>
                </div>
            </div>
        </div>
    );
}